import { UrlConfig } from 'config';
import { ApiResponse } from 'models/Api/ApiResponse';
import { AjaxService } from 'services';
import { ScrapejobModel} from 'models/scrapejob/ScrapejobModel';
import {ScrapejobSummaryModel} from 'models/scrapejob/ScrapejobSummaryModel';
import {ScrapesiteModel} from 'models/scrapejob/ScrapesiteModel';
import { RawScrapejobSummaryData, RawScrapejobDetailData, RawScrapesiteData } from 'models/scrapejob/types';
import { PaginationModel } from 'models';
import { RawPaginationData } from 'models/Pagination/Pagination';
import { ProcessStatus } from 'models/types';

export class ScrapejobApiClient {
  public static async getScrapejobById(id: string) {
    const url = UrlConfig.SCRAPEJOB.SCRAPEJOB_BY_ID + `${id}`;
    const response: ApiResponse<RawScrapejobDetailData> = await AjaxService.getData(url);
    const scrapejob = new ScrapejobModel(response.data);
    return scrapejob;
  }

  public static async getScrapejobsByStatus(status: string) {
    const url = UrlConfig.SCRAPEJOB.SCRAPEJOB_BY_STATUS + `${status}`;
    const response: ApiResponse<RawScrapejobSummaryData[]> = await AjaxService.getData(url);

    // Fetch scrape sites to get images
    const scrapesites = await this.getAllScrapesites();
    const scrapesiteMap = new Map(scrapesites.map(site => [site.id, site.image]));

    const scrapejobList = response.data.map(rawScrapejob => {
      const image = scrapesiteMap.get(rawScrapejob.site_id) || '';
      return new ScrapejobSummaryModel(rawScrapejob, image);
    });
    return scrapejobList;
  }

  public static async getDetailedStatusScrapejobs(
    status: ProcessStatus,
    page?: number,
    perPage?: number
  ): Promise<PaginationModel<ScrapejobModel>> {
    const params = new URLSearchParams();
    if (page != null) params.set('page', String(page));
    if (perPage != null) params.set('per_page', String(perPage));
    const query = params.toString();
    const url =
      UrlConfig.SCRAPEJOB.SCRAPEJOB_DETAILED_STATUS +
      `${status}` +
      (query ? `?${query}` : '');
    const response: ApiResponse<RawPaginationData<RawScrapejobDetailData>> = await AjaxService.getData(url);
    const paginatedResponse: PaginationModel<RawScrapejobDetailData> = new PaginationModel(response.data);
    const formattedScrapejobs = paginatedResponse.data.map((rawScrapejob: RawScrapejobDetailData) => new ScrapejobModel(rawScrapejob));
    const formattedPaginatedResponse: PaginationModel<ScrapejobModel> = new PaginationModel({ ...response.data, data: formattedScrapejobs, fetched: true });
    return formattedPaginatedResponse;
  }

  public static async getAllScrapejobs() {
    const url = UrlConfig.SCRAPEJOB.SCRAPEJOB_ALL;
    const response: ApiResponse<RawScrapejobSummaryData[]> = await AjaxService.getData(url);

    // Fetch scrape sites to get images
    const scrapesites = await this.getAllScrapesites();
    const scrapesiteMap = new Map(scrapesites.map(site => [site.id, site.image]));

    const scrapejobList = response.data.map(rawScrapejob => {
      const image = scrapesiteMap.get(rawScrapejob.site_id) || '';
      return new ScrapejobSummaryModel(rawScrapejob, image);
    });
    return scrapejobList;
  }

  public static async getAllScrapesites() {
    const url = UrlConfig.SCRAPESITE.SCRAPESITE_ALL;
    const response: ApiResponse<RawScrapesiteData[]> = await AjaxService.getData(url);
    const scrapesiteList = response.data.map(rawScrapesite => new ScrapesiteModel(rawScrapesite));
    return scrapesiteList;
  }

  public static async approveScrapejob(payload: any) {
    const url = UrlConfig.SCRAPEJOB.SCRAPEJOB_APPROVE;
    const id = payload.scrapejob_id;
    await AjaxService.postData(url, payload);
    return id;
  }

  public static async rejectScrapejob(payload: any) {
    const url = UrlConfig.SCRAPEJOB.SCRAPEJOB_REJECT;
    const id = payload.scrapejob_id;
    await AjaxService.postData(url, payload);
    return id;
  }

  public static async sanitizeScrapejob(payload: any) {
    const url = UrlConfig.SCRAPEJOB.SCRAPEJOB_SANITIZE;
    const id = payload.scrapejob_id;
    await AjaxService.postData(url, payload);
    return id;
  }
}
